import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { tx, txf, useLang, txLocale } from "../lib/i18n";
import { PageLoading } from "../components/PageLoading";

const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(txLocale(), { dateStyle: "long" }) : "—");
const slug = (i) => `sec-${i + 1}`;
const plain = (h) => h.replace(/<[^>]+>/g, " ").toLowerCase();

const Meta = ({ p }) => { useLang(); return (
  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--mv-dim)]" data-testid="policy-meta">
    <span data-testid="policy-effective-date">{tx("Effective date")}: <b className="text-[var(--mv-muted)]">{fmt(p.effective_date)}</b></span>
    {p.last_updated && <span data-testid="policy-last-updated">{tx("Last updated")}: <b className="text-[var(--mv-muted)]">{fmt(p.last_updated)}</b></span>}
    {p.version && <span data-testid="policy-version">{txf("Version {0}", p.version)}</span>}
    {p.legal_name && <span data-testid="policy-operator">{tx("Operated by")} {p.legal_name}</span>}
  </div>
); };

const Toc = ({ sections, q, setQ }) => { useLang(); return (
  <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="policy-toc">
    <div className="mb-4 flex items-center gap-2 rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-card)] px-3"><Search className="h-4 w-4 text-[var(--mv-dim)]" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx("Search this policy")} aria-label={tx("Search this policy")} className="w-full bg-transparent py-2 text-sm outline-none" data-testid="policy-search" /></div>
    <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[var(--mv-dim)]">{tx("On this page")}</p>
    <ol className="space-y-1.5 text-xs">{sections.map((s, i) => <li key={s.id}><a href={`#${slug(i)}`} className="text-[var(--mv-muted)] hover:text-[var(--mv-teal)]">{i + 1}. {s.heading}</a></li>)}</ol>
  </aside>
); };

export default function PolicyPage() { const lang = useLang();
  const { key } = useParams();
  const [sp] = useSearchParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const preview = sp.get("preview") === "draft";
  useEffect(() => { setP(null); setErr(false); api.get(`/site/policies/${key}`, { params: preview ? { preview: "draft", lang: sp.get("lang") || undefined } : {} }).then((r) => setP(r.data)).catch(() => setErr(true)); }, [key, lang, preview, sp]);
  const shown = useMemo(() => (p?.sections || []).map((s, i) => ({ ...s, i })).filter((s) => !q || plain(s.heading + " " + s.html).includes(q.toLowerCase())), [p, q]);
  if (err) return <div className="mv-container py-24 text-[var(--mv-muted)]" data-testid="policy-not-found">{tx("Policy not found.")} <Link to="/" className="text-[var(--mv-teal)]">{tx("Home")}</Link></div>;
  if (!p) return <div className="mv-container py-24 text-[var(--mv-muted)]"><PageLoading /></div>;
  return (
    <div className="mv-container pb-24 pt-10" data-testid={`policy-page-${key}`}>
      {preview && <p className="mb-6 rounded-xl border border-amber-300/50 bg-amber-300/10 px-4 py-2 text-xs text-amber-200" data-testid="policy-preview-banner">{tx("DRAFT PREVIEW — only Super Admin can see this. Highlighted items must be completed before publishing.")}</p>}
      <p className="text-[11px] tracking-[0.3em] text-[var(--mv-teal)]">{(p.company || "MIVO").toUpperCase()} · {tx("LEGAL")}</p>
      <h1 className="mt-2 text-4xl font-semibold sm:text-5xl" data-testid="policy-title">{p.published || preview ? p.title : tx(p.title)}</h1>
      {(p.published || preview) && <Meta p={p} />}
      {p.fallback_to_english && <p className="mt-4 rounded-xl border border-[var(--mv-border-2)] px-4 py-2 text-xs text-[var(--mv-muted)]" data-testid="policy-fallback-notice">{tx("A reviewed translation of this policy is not available yet. The English version below is the authoritative version.")}</p>}
      {!p.published && !preview ? (
        <div className="mt-10 max-w-2xl rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-6 text-sm text-[var(--mv-muted)]" data-testid="policy-empty">
          <p>{tx("This policy is being finalised and reviewed. It will be published here once it has been approved.")}</p>
          {p.support_email && <p className="mt-2">{tx("Questions? Contact")} <a className="text-[var(--mv-teal)]" href={`mailto:${p.support_email}`}>{p.support_email}</a></p>}
        </div>
      ) : (
        <div className="mt-10 grid gap-10 lg:grid-cols-[240px_1fr]">
          <Toc sections={p.sections} q={q} setQ={setQ} />
          <div className="max-w-3xl" data-testid="policy-body">
            {shown.length === 0 && <p className="text-sm text-[var(--mv-dim)]" data-testid="policy-search-empty">{tx("No sections match your search.")}</p>}
            {shown.map((s) => (
              <section key={s.id} id={slug(s.i)} className="scroll-mt-28 border-b border-[var(--mv-border)] py-6 first:pt-0" data-testid={`policy-section-${s.i + 1}`}>
                <h2 className="text-lg font-semibold text-white">{s.i + 1}. {s.heading}</h2>
                <div className="mv-prose mt-3" dangerouslySetInnerHTML={{ __html: s.html }} />
              </section>))}
          </div>
        </div>
      )}
      <div className="mt-12 flex flex-wrap gap-3" data-testid="policy-related">
        <span className="text-xs text-[var(--mv-dim)]">{tx("Related policies")}:</span>
        {p.related.map((r) => <Link key={r.key} to={`/policies/${r.key}`} className="rounded-full border border-[var(--mv-border-2)] px-3 py-1 text-xs hover:border-[var(--mv-teal)]" data-testid={`policy-related-${r.key}`}>{tx(r.title)}</Link>)}
      </div>
    </div>
  );
}
